import { useState } from "react";
import { Link } from "react-router-dom";
import { 
  Calculator, Car, Train, Plane, Bus, Bike, Zap, Home, Factory, 
  Utensils, ShoppingBag, Tv, Smartphone, ArrowRight, Leaf, Info
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Emission factors (kg CO2 per unit)
const EMISSION_FACTORS = {
  // Transportation (per km)
  car_petrol: 0.192,
  car_diesel: 0.171,
  car_electric: 0.053,
  motorcycle: 0.103,
  bus: 0.089,
  train: 0.041,
  metro: 0.033,
  flight_domestic: 0.255,
  flight_international: 0.195,
  bicycle: 0,
  walking: 0,
  
  // Home Energy
  electricity: 0.82, // per kWh (India grid average)
  lpg: 2.98, // per kg
  png: 2.0, // per cubic meter
  diesel_generator: 2.68, // per liter
  
  // Food & Diet (per meal)
  meat_heavy: 3.3,
  meat_medium: 2.5,
  vegetarian: 1.7,
  vegan: 1.5,
  
  // Shopping & Lifestyle
  clothing_item: 10, // per item
  electronics_small: 50, // smartphone, etc.
  electronics_large: 200, // TV, laptop, etc.
  furniture: 100, // per item
  
  // Other
  hotel_night: 21,
  streaming_hour: 0.036,
  internet_hour: 0.02,
};

export default function EmissionCalculatorPage() {
  const [activeTab, setActiveTab] = useState("transport");
  const [results, setResults] = useState(null);
  const [calculating, setCalculating] = useState(false);
  
  // Transport
  const [transport, setTransport] = useState({
    car_km: "",
    car_type: "car_petrol",
    motorcycle_km: "",
    bus_km: "",
    train_km: "",
    metro_km: "",
    domestic_flights: "",
    international_flights: "",
  });
  
  // Home Energy
  const [energy, setEnergy] = useState({
    electricity_kwh: "",
    lpg_kg: "",
    png_cubic_m: "",
    generator_liters: "",
  });
  
  // Food & Diet
  const [food, setFood] = useState({
    meals_per_day: "3",
    diet_type: "vegetarian",
  });
  
  // Lifestyle
  const [lifestyle, setLifestyle] = useState({
    clothing_items: "",
    electronics_small: "",
    electronics_large: "",
    streaming_hours: "",
    hotel_nights: "",
  });

  const calculateEmissions = () => {
    setCalculating(true);
    
    // Monthly calculations
    const transportEmissions = 
      (parseFloat(transport.car_km) || 0) * EMISSION_FACTORS[transport.car_type] +
      (parseFloat(transport.motorcycle_km) || 0) * EMISSION_FACTORS.motorcycle +
      (parseFloat(transport.bus_km) || 0) * EMISSION_FACTORS.bus +
      (parseFloat(transport.train_km) || 0) * EMISSION_FACTORS.train +
      (parseFloat(transport.metro_km) || 0) * EMISSION_FACTORS.metro +
      (parseFloat(transport.domestic_flights) || 0) * 500 * EMISSION_FACTORS.flight_domestic +
      (parseFloat(transport.international_flights) || 0) * 4000 * EMISSION_FACTORS.flight_international;
    
    const energyEmissions = 
      (parseFloat(energy.electricity_kwh) || 0) * EMISSION_FACTORS.electricity +
      (parseFloat(energy.lpg_kg) || 0) * EMISSION_FACTORS.lpg +
      (parseFloat(energy.png_cubic_m) || 0) * EMISSION_FACTORS.png +
      (parseFloat(energy.generator_liters) || 0) * EMISSION_FACTORS.diesel_generator;
    
    const mealsPerMonth = (parseFloat(food.meals_per_day) || 3) * 30;
    const foodEmissions = mealsPerMonth * EMISSION_FACTORS[food.diet_type];
    
    const lifestyleEmissions = 
      (parseFloat(lifestyle.clothing_items) || 0) * EMISSION_FACTORS.clothing_item +
      (parseFloat(lifestyle.electronics_small) || 0) * EMISSION_FACTORS.electronics_small +
      (parseFloat(lifestyle.electronics_large) || 0) * EMISSION_FACTORS.electronics_large +
      (parseFloat(lifestyle.streaming_hours) || 0) * EMISSION_FACTORS.streaming_hour +
      (parseFloat(lifestyle.hotel_nights) || 0) * EMISSION_FACTORS.hotel_night;
    
    const monthlyTotal = transportEmissions + energyEmissions + foodEmissions + lifestyleEmissions;
    const yearlyTotal = monthlyTotal * 12;
    
    setResults({
      transport: transportEmissions,
      energy: energyEmissions,
      food: foodEmissions,
      lifestyle: lifestyleEmissions,
      monthlyTotal,
      yearlyTotal,
      offsetCost: yearlyTotal * 10, // ₹10 per kg
    });
    
    setCalculating(false);
  };

  const resetCalculator = () => {
    setTransport({
      car_km: "", car_type: "car_petrol", motorcycle_km: "", bus_km: "",
      train_km: "", metro_km: "", domestic_flights: "", international_flights: "",
    });
    setEnergy({ electricity_kwh: "", lpg_kg: "", png_cubic_m: "", generator_liters: "" });
    setFood({ meals_per_day: "3", diet_type: "vegetarian" });
    setLifestyle({ clothing_items: "", electronics_small: "", electronics_large: "", streaming_hours: "", hotel_nights: "" });
    setResults(null);
  };

  return (
    <div className="min-h-screen pt-20 bg-white" data-testid="emission-calculator-page">
      {/* Hero */}
      <section className="py-12 lg:py-16 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-6 shadow-sm">
            <Calculator className="h-4 w-4" />
            Comprehensive Carbon Calculator
          </div>
          <h1 className="font-montserrat text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            Estimate Your Carbon Footprint
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Calculate your carbon emissions across transportation, home energy, food, and lifestyle. 
            Understand your impact and take action to offset it.
          </p>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-12 lg:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Input Section */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="font-montserrat">Enter Your Monthly Data</CardTitle>
                </CardHeader>
                <CardContent>
                  <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="grid w-full grid-cols-4 mb-6">
                      <TabsTrigger value="transport" className="flex items-center gap-1">
                        <Car className="h-4 w-4" />
                        <span className="hidden sm:inline">Transport</span>
                      </TabsTrigger>
                      <TabsTrigger value="energy" className="flex items-center gap-1">
                        <Zap className="h-4 w-4" />
                        <span className="hidden sm:inline">Energy</span>
                      </TabsTrigger>
                      <TabsTrigger value="food" className="flex items-center gap-1">
                        <Utensils className="h-4 w-4" />
                        <span className="hidden sm:inline">Food</span>
                      </TabsTrigger>
                      <TabsTrigger value="lifestyle" className="flex items-center gap-1">
                        <ShoppingBag className="h-4 w-4" />
                        <span className="hidden sm:inline">Lifestyle</span>
                      </TabsTrigger>
                    </TabsList>

                    {/* Transportation */}
                    <TabsContent value="transport" className="space-y-4">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label className="flex items-center gap-2">
                            <Car className="h-4 w-4 text-slate-500" />
                            Car Distance (km/month)
                          </Label>
                          <Input
                            type="number"
                            placeholder="e.g., 500"
                            value={transport.car_km}
                            onChange={(e) => setTransport({ ...transport, car_km: e.target.value })}
                            className="mt-1"
                            data-testid="input-car-km"
                          />
                        </div>
                        <div>
                          <Label>Car Type</Label>
                          <Select
                            value={transport.car_type}
                            onValueChange={(value) => setTransport({ ...transport, car_type: value })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="car_petrol">Petrol Car</SelectItem>
                              <SelectItem value="car_diesel">Diesel Car</SelectItem>
                              <SelectItem value="car_electric">Electric Car</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="flex items-center gap-2">
                            <Bike className="h-4 w-4 text-slate-500" />
                            Motorcycle Distance (km/month)
                          </Label>
                          <Input
                            type="number"
                            placeholder="e.g., 200"
                            value={transport.motorcycle_km}
                            onChange={(e) => setTransport({ ...transport, motorcycle_km: e.target.value })}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="flex items-center gap-2">
                            <Bus className="h-4 w-4 text-slate-500" />
                            Bus Distance (km/month)
                          </Label>
                          <Input
                            type="number"
                            placeholder="e.g., 100"
                            value={transport.bus_km}
                            onChange={(e) => setTransport({ ...transport, bus_km: e.target.value })}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="flex items-center gap-2">
                            <Train className="h-4 w-4 text-slate-500" />
                            Train Distance (km/month)
                          </Label>
                          <Input
                            type="number"
                            placeholder="e.g., 300"
                            value={transport.train_km}
                            onChange={(e) => setTransport({ ...transport, train_km: e.target.value })}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Metro Distance (km/month)</Label>
                          <Input
                            type="number"
                            placeholder="e.g., 50"
                            value={transport.metro_km}
                            onChange={(e) => setTransport({ ...transport, metro_km: e.target.value })}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="flex items-center gap-2">
                            <Plane className="h-4 w-4 text-slate-500" />
                            Domestic Flights (per month)
                          </Label>
                          <Input
                            type="number"
                            placeholder="e.g., 2"
                            value={transport.domestic_flights}
                            onChange={(e) => setTransport({ ...transport, domestic_flights: e.target.value })}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="flex items-center gap-2">
                            <Plane className="h-4 w-4 text-sky-500" />
                            International Flights (per month)
                          </Label>
                          <Input
                            type="number"
                            placeholder="e.g., 1"
                            value={transport.international_flights}
                            onChange={(e) => setTransport({ ...transport, international_flights: e.target.value })}
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </TabsContent>

                    {/* Energy */}
                    <TabsContent value="energy" className="space-y-4">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label className="flex items-center gap-2">
                            <Zap className="h-4 w-4 text-amber-500" />
                            Electricity (kWh/month)
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger><Info className="h-3 w-3 text-slate-400" /></TooltipTrigger>
                                <TooltipContent>Check your electricity bill for monthly units</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </Label>
                          <Input
                            type="number"
                            placeholder="e.g., 200"
                            value={energy.electricity_kwh}
                            onChange={(e) => setEnergy({ ...energy, electricity_kwh: e.target.value })}
                            className="mt-1"
                            data-testid="input-electricity"
                          />
                        </div>
                        <div>
                          <Label>LPG Cylinder (kg/month)</Label>
                          <Input
                            type="number"
                            placeholder="e.g., 14.2 (1 cylinder)"
                            value={energy.lpg_kg}
                            onChange={(e) => setEnergy({ ...energy, lpg_kg: e.target.value })}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>PNG / Natural Gas (m³/month)</Label>
                          <Input
                            type="number"
                            placeholder="e.g., 20"
                            value={energy.png_cubic_m}
                            onChange={(e) => setEnergy({ ...energy, png_cubic_m: e.target.value })}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Diesel Generator (liters/month)</Label>
                          <Input
                            type="number"
                            placeholder="e.g., 10"
                            value={energy.generator_liters}
                            onChange={(e) => setEnergy({ ...energy, generator_liters: e.target.value })}
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </TabsContent>

                    {/* Food */}
                    <TabsContent value="food" className="space-y-4">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label>Meals Per Day</Label>
                          <Select
                            value={food.meals_per_day}
                            onValueChange={(value) => setFood({ ...food, meals_per_day: value })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="2">2 meals</SelectItem>
                              <SelectItem value="3">3 meals</SelectItem>
                              <SelectItem value="4">4 meals</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Diet Type</Label>
                          <Select
                            value={food.diet_type}
                            onValueChange={(value) => setFood({ ...food, diet_type: value })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="meat_heavy">Heavy Meat (daily non-veg)</SelectItem>
                              <SelectItem value="meat_medium">Medium Meat (2-3x/week non-veg)</SelectItem>
                              <SelectItem value="vegetarian">Vegetarian</SelectItem>
                              <SelectItem value="vegan">Vegan</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="p-4 bg-emerald-50 rounded-lg">
                        <p className="text-sm text-emerald-800">
                          <strong>Did you know?</strong> Shifting from a meat-heavy to vegetarian diet can reduce your food-related emissions by almost 50%.
                        </p>
                      </div>
                    </TabsContent>

                    {/* Lifestyle */}
                    <TabsContent value="lifestyle" className="space-y-4">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label className="flex items-center gap-2">
                            <ShoppingBag className="h-4 w-4 text-pink-500" />
                            New Clothing Items (per month)
                          </Label>
                          <Input
                            type="number"
                            placeholder="e.g., 2"
                            value={lifestyle.clothing_items}
                            onChange={(e) => setLifestyle({ ...lifestyle, clothing_items: e.target.value })}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="flex items-center gap-2">
                            <Smartphone className="h-4 w-4 text-slate-500" />
                            Small Electronics Purchased
                          </Label>
                          <Input
                            type="number"
                            placeholder="e.g., 0"
                            value={lifestyle.electronics_small}
                            onChange={(e) => setLifestyle({ ...lifestyle, electronics_small: e.target.value })}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="flex items-center gap-2">
                            <Tv className="h-4 w-4 text-slate-500" />
                            Large Electronics (TV, Laptop)
                          </Label>
                          <Input
                            type="number"
                            placeholder="e.g., 0"
                            value={lifestyle.electronics_large}
                            onChange={(e) => setLifestyle({ ...lifestyle, electronics_large: e.target.value })}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Streaming Hours (per month)</Label>
                          <Input
                            type="number"
                            placeholder="e.g., 50"
                            value={lifestyle.streaming_hours}
                            onChange={(e) => setLifestyle({ ...lifestyle, streaming_hours: e.target.value })}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="flex items-center gap-2">
                            <Home className="h-4 w-4 text-purple-500" />
                            Hotel Nights (per month)
                          </Label>
                          <Input
                            type="number"
                            placeholder="e.g., 2"
                            value={lifestyle.hotel_nights}
                            onChange={(e) => setLifestyle({ ...lifestyle, hotel_nights: e.target.value })}
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>

                  <div className="flex gap-4 mt-8">
                    <Button 
                      onClick={calculateEmissions}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      disabled={calculating}
                      data-testid="calculate-emissions-btn"
                    >
                      {calculating ? "Calculating..." : "Calculate My Footprint"}
                    </Button>
                    <Button variant="outline" onClick={resetCalculator}>
                      Reset
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Results Section */}
            <div className="lg:col-span-1">
              {results ? (
                <Card className="sticky top-24 bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200" data-testid="calculation-results">
                  <CardHeader>
                    <CardTitle className="font-montserrat text-emerald-900">Your Carbon Footprint</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Total */}
                    <div className="text-center p-6 bg-white rounded-2xl shadow-sm">
                      <p className="text-5xl font-bold text-emerald-700">
                        {results.yearlyTotal.toFixed(0).toLocaleString()}
                      </p>
                      <p className="text-slate-600 mt-1">kg CO₂ per year</p>
                      <p className="text-sm text-slate-500 mt-2">
                        ({(results.monthlyTotal).toFixed(0)} kg/month)
                      </p>
                    </div>

                    {/* Breakdown */}
                    <div className="space-y-3">
                      <p className="font-semibold text-slate-700 text-sm">Monthly Breakdown:</p>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center p-2 bg-white/60 rounded-lg">
                          <span className="text-sm flex items-center gap-2">
                            <Car className="h-4 w-4 text-blue-600" /> Transportation
                          </span>
                          <span className="font-medium">{results.transport.toFixed(1)} kg</span>
                        </div>
                        <div className="flex justify-between items-center p-2 bg-white/60 rounded-lg">
                          <span className="text-sm flex items-center gap-2">
                            <Zap className="h-4 w-4 text-amber-500" /> Home Energy
                          </span>
                          <span className="font-medium">{results.energy.toFixed(1)} kg</span>
                        </div>
                        <div className="flex justify-between items-center p-2 bg-white/60 rounded-lg">
                          <span className="text-sm flex items-center gap-2">
                            <Utensils className="h-4 w-4 text-orange-500" /> Food & Diet
                          </span>
                          <span className="font-medium">{results.food.toFixed(1)} kg</span>
                        </div>
                        <div className="flex justify-between items-center p-2 bg-white/60 rounded-lg">
                          <span className="text-sm flex items-center gap-2">
                            <ShoppingBag className="h-4 w-4 text-pink-500" /> Lifestyle
                          </span>
                          <span className="font-medium">{results.lifestyle.toFixed(1)} kg</span>
                        </div>
                      </div>
                    </div>

                    {/* Offset CTA */}
                    <div className="p-4 bg-white rounded-xl border border-emerald-200 text-center">
                      <p className="text-sm text-slate-600 mb-1">Offset your yearly emissions for</p>
                      <p className="text-2xl font-bold text-emerald-700">₹{results.offsetCost.toFixed(0).toLocaleString()}</p>
                    </div>

                    <Link to="/initiatives/bharat-climate">
                      <Button className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="offset-now-btn">
                        <Leaf className="mr-2 h-4 w-4" />
                        Offset Your Emissions
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ) : (
                <Card className="sticky top-24 bg-slate-50">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Calculator className="h-8 w-8 text-emerald-600" />
                    </div>
                    <h3 className="font-semibold text-slate-900 mb-2">Calculate Your Impact</h3>
                    <p className="text-sm text-slate-600">
                      Enter your monthly consumption data across categories to see your estimated carbon footprint.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Emission Factors Reference */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-montserrat text-2xl font-bold text-slate-900 mb-6 text-center">
            Emission Factors Used
          </h2>
          <div className="grid md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-xl">
              <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                <Car className="h-4 w-4 text-blue-600" /> Transport
              </h3>
              <ul className="text-xs text-slate-600 space-y-1">
                <li>Petrol Car: 192g/km</li>
                <li>Diesel Car: 171g/km</li>
                <li>Electric Car: 53g/km</li>
                <li>Train: 41g/km</li>
                <li>Flight: 195-255g/km</li>
              </ul>
            </div>
            <div className="bg-white p-4 rounded-xl">
              <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                <Zap className="h-4 w-4 text-amber-500" /> Energy
              </h3>
              <ul className="text-xs text-slate-600 space-y-1">
                <li>Electricity: 820g/kWh</li>
                <li>LPG: 2.98kg/kg</li>
                <li>PNG: 2kg/m³</li>
                <li>Diesel Gen: 2.68kg/L</li>
              </ul>
            </div>
            <div className="bg-white p-4 rounded-xl">
              <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                <Utensils className="h-4 w-4 text-orange-500" /> Food
              </h3>
              <ul className="text-xs text-slate-600 space-y-1">
                <li>Meat Heavy: 3.3kg/meal</li>
                <li>Meat Medium: 2.5kg/meal</li>
                <li>Vegetarian: 1.7kg/meal</li>
                <li>Vegan: 1.5kg/meal</li>
              </ul>
            </div>
            <div className="bg-white p-4 rounded-xl">
              <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                <ShoppingBag className="h-4 w-4 text-pink-500" /> Lifestyle
              </h3>
              <ul className="text-xs text-slate-600 space-y-1">
                <li>Clothing: ~10kg/item</li>
                <li>Hotel: 21kg/night</li>
                <li>Electronics: 50-200kg</li>
                <li>Streaming: 36g/hour</li>
              </ul>
            </div>
          </div>
          <p className="text-xs text-slate-500 text-center mt-4">
            * Based on India-specific and international emission factors. Actual values may vary.
          </p>
        </div>
      </section>
    </div>
  );
}
